/** CMS page block types for storefront articles. */

import { LEGAL_PAGES } from "./legal-pages.js";

export type CmsBlockKind =
  | "heading"
  | "paragraph"
  | "image"
  | "quote"
  | "divider"
  | "cta";

export interface CmsBlockBase {
  id: string;
  enabled?: boolean;
}

export type CmsBlock =
  | (CmsBlockBase & { kind: "heading"; level: 1 | 2; text: string })
  | (CmsBlockBase & { kind: "paragraph"; text: string })
  | (CmsBlockBase & { kind: "image"; url: string; alt?: string; caption?: string })
  | (CmsBlockBase & { kind: "quote"; text: string; attribution?: string })
  | (CmsBlockBase & { kind: "divider" })
  | (CmsBlockBase & { kind: "cta"; label: string; href: string });

export const CMS_BLOCK_LABELS: Record<CmsBlockKind, string> = {
  heading: "Heading",
  paragraph: "Paragraph",
  image: "Image",
  quote: "Quote",
  divider: "Divider",
  cta: "Call to action",
};

export const CMS_BLOCK_KINDS = Object.keys(CMS_BLOCK_LABELS) as CmsBlockKind[];

export const CMS_HERO_LINK_TYPES = ["NONE", "ARTICLE", "EXTERNAL", "INTERNAL"] as const;
export type CmsHeroLinkType = (typeof CMS_HERO_LINK_TYPES)[number];

export const CMS_HERO_LINK_LABELS: Record<CmsHeroLinkType, string> = {
  NONE: "No link",
  ARTICLE: "CMS article",
  EXTERNAL: "External URL",
  INTERNAL: "Internal route",
};

export const CMS_INTERNAL_PATH_PRESETS = [
  { value: "/", label: "Home" },
  { value: "/shop", label: "Shop" },
  { value: "/packs", label: "Packs" },
  { value: "/raffles", label: "Raffles" },
  { value: "/auctions", label: "Auctions" },
  { value: "/orders", label: "Orders" },
  { value: "/wallet", label: "Wallet" },
] as const;

const LEGAL_SLUGS = new Set<string>(LEGAL_PAGES.map((p) => p.slug));

function blockId(kind: CmsBlockKind): string {
  return `${kind}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createCmsBlock(kind: CmsBlockKind): CmsBlock {
  const base = { id: blockId(kind), enabled: true };
  switch (kind) {
    case "heading":
      return { ...base, kind, level: 2, text: "Section heading" };
    case "paragraph":
      return { ...base, kind, text: "Write your content here." };
    case "image":
      return { ...base, kind, url: "", alt: "", caption: "" };
    case "quote":
      return { ...base, kind, text: "", attribution: "" };
    case "divider":
      return { ...base, kind };
    case "cta":
      return { ...base, kind, label: "Learn more", href: "/shop" };
    default:
      return { ...base, kind } as CmsBlock;
  }
}

export function parseCmsBlocks(raw: unknown): CmsBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (b): b is CmsBlock =>
      typeof b === "object" &&
      b !== null &&
      typeof (b as CmsBlock).id === "string" &&
      typeof (b as CmsBlock).kind === "string",
  );
}

export function slugifyTitle(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidCmsSlug(slug: string): boolean {
  return slug.length >= 2 && slug.length <= 80 && SLUG_PATTERN.test(slug);
}

export function validateCmsSlug(slug: string): string | null {
  if (!isValidCmsSlug(slug)) {
    return "Slug must be 2–80 lowercase letters, numbers, and hyphens";
  }
  if (LEGAL_SLUGS.has(slug)) {
    return "Slug conflicts with a reserved legal page";
  }
  return null;
}

export function isAllowedInternalPath(path: string): boolean {
  if (!path.startsWith("/")) return false;
  if (path.includes("://") || path.startsWith("//")) return false;
  if (path === "/") return true;
  const allowedPrefixes = ["/shop", "/packs", "/raffles", "/auctions", "/pages", "/orders", "/wallet", "/cart", "/track"];
  return allowedPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function resolveHeroHref(slide: {
  linkType: CmsHeroLinkType;
  cmsPageSlug?: string | null;
  externalUrl?: string | null;
  internalPath?: string | null;
}): { href: string; external: boolean } | null {
  switch (slide.linkType) {
    case "NONE":
      return null;
    case "ARTICLE":
      return slide.cmsPageSlug ? { href: `/pages/${slide.cmsPageSlug}`, external: false } : null;
    case "EXTERNAL":
      return slide.externalUrl?.trim() ? { href: slide.externalUrl.trim(), external: true } : null;
    case "INTERNAL":
      return slide.internalPath?.trim() && isAllowedInternalPath(slide.internalPath.trim())
        ? { href: slide.internalPath.trim(), external: false }
        : null;
    default:
      return null;
  }
}

/** Settings keys for the storefront hero shown when CMS is off or no slides exist. */
export const CMS_HERO_FALLBACK_SETTING_KEYS = {
  eyebrow: "cms.hero_fallback.eyebrow",
  headline: "cms.hero_fallback.headline",
  description: "cms.hero_fallback.description",
} as const;

export interface CmsHeroFallback {
  eyebrow: string;
  headline: string;
  description: string;
}

export const CMS_HERO_FALLBACK_DEFAULTS: CmsHeroFallback = {
  eyebrow: "Mr Gaole",
  headline: "Connecting passion through collectibles",
  description:
    "Rip packs, win raffles, snipe auctions and trade the cards you love — all in one place.",
};
